// v-select's `append-to-body` teleports the dropdown `<ul>` to <body> so it escapes
// clipping from an ancestor `overflow: auto` (e.g. BaseModal's `.modal-body`). Once
// teleported the `<ul>` also drops out of the DOM subtree it used to inherit from, losing
// both the app `font-family` (#app in App.vue) and every `--vs-*` custom property a wrapper
// or scoped rule set on it (option colors, padding, max-height, ...). This factory rebuilds
// that context on the teleported node: it re-applies the app font, copies the vue-select CSS
// variables off the still-in-place toggle, and finally applies any explicit overrides.
//
// It is wired as the GLOBAL default `calculate-position` for every <v-select> in main.ts,
// so any dropdown escapes ancestor clipping without per-usage work. Per-instance callers can
// still pass their own overrides (minWidth / maxHeight / cssVars).
export const APP_FONT_FAMILY = "'Prompt', Avenir, Helvetica, Arial, sans-serif";

// vue-select CSS variables that style the dropdown <ul> and its options. Custom properties
// inherit through the DOM, so teleporting to <body> drops whatever an ancestor set. We read
// their resolved values off the toggle (which is still inside the original, styled subtree)
// and re-apply them inline on the teleported <ul> so per-screen theming survives the teleport.
const VS_DROPDOWN_VARS = [
  '--vs-font-size',
  '--vs-line-height',
  '--vs-colors--lightest',
  '--vs-colors--light',
  '--vs-colors--dark',
  '--vs-colors--darkest',
  '--vs-dropdown-bg',
  '--vs-dropdown-color',
  '--vs-dropdown-z-index',
  '--vs-dropdown-min-width',
  '--vs-dropdown-max-height',
  '--vs-dropdown-box-shadow',
  '--vs-dropdown-option-bg',
  '--vs-dropdown-option-color',
  '--vs-dropdown-option-padding',
  '--vs-dropdown-option--active-bg',
  '--vs-dropdown-option--active-color',
  '--vs-dropdown-option--deselect-bg',
  '--vs-dropdown-option--deselect-color',
];

// vue-select passes its own instance as the 2nd arg; `$refs.toggle` is the toggle element.
interface VSelectComponentLike {
  $refs?: { toggle?: HTMLElement };
}

export function createVSelectDropdownPositioner(
  overrides: { minWidth?: string; maxHeight?: string; cssVars?: Record<string, string> } = {},
) {
  return function positionDropdown(
    dropdownList: HTMLElement,
    component: unknown,
    { width, top, left }: { width: string; top: string; left: string },
  ) {
    dropdownList.style.width = width;
    dropdownList.style.top = top;
    dropdownList.style.left = left;
    dropdownList.style.zIndex = '9999';
    dropdownList.style.fontFamily = APP_FONT_FAMILY;

    // Re-apply the vue-select CSS variables the <ul> would have inherited before the teleport,
    // read from the toggle which is still inside the original (styled) DOM subtree.
    const toggle = (component as VSelectComponentLike | undefined)?.$refs?.toggle;
    if (toggle) {
      const computed = getComputedStyle(toggle);
      for (const name of VS_DROPDOWN_VARS) {
        const value = computed.getPropertyValue(name);
        if (value && value.trim()) dropdownList.style.setProperty(name, value.trim());
      }
    }

    // Explicit per-instance overrides win over the inherited values above.
    if (overrides.minWidth) dropdownList.style.minWidth = overrides.minWidth;
    if (overrides.maxHeight) dropdownList.style.maxHeight = overrides.maxHeight;
    if (overrides.cssVars) {
      for (const [name, value] of Object.entries(overrides.cssVars)) {
        dropdownList.style.setProperty(name, value);
      }
    }
  };
}
